import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { Mail, CheckCircle2, XCircle, Loader2, LogIn, ArrowRight } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

// ─────────────────────────────────────────────────────────────────
// Accept invite — /accept-invite?token=…
// Shows who the invite is for (public peek), then lets the signed-in
// user redeem it. Acceptance requires an account whose email matches
// the invite (server-enforced).
// ─────────────────────────────────────────────────────────────────

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setActiveWorkspace = useAuthStore((s) => s.setActiveWorkspace);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [accepted, setAccepted] = useState(false);

  const peekQuery = useQuery({
    queryKey: ['workspace-invite', token],
    queryFn: () => api.workspaces.invitePeek(token),
    enabled: Boolean(token),
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => api.workspaces.acceptInvite(token),
    onSuccess: (result) => {
      setAccepted(true);
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
      if (result.workspace) {
        setActiveWorkspace(result.workspace.id);
      }
    },
  });

  const peek = peekQuery.data;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="surface-row w-full max-w-md px-6 py-8 text-center">
        {!token || peekQuery.isError ? (
          <Block
            icon={<XCircle size={22} className="text-danger" />}
            title="Invite not found"
            message={
              token ? 'This invite link is invalid or has expired.' : 'No invite token was provided.'
            }
          />
        ) : peekQuery.isLoading || !peek ? (
          <div className="flex justify-center py-6">
            <Loader2 size={24} className="animate-spin text-accent" />
          </div>
        ) : accepted || acceptMutation.isSuccess ? (
          <>
            <CheckCircle2 size={26} className="mx-auto text-accent" />
            <h2 className="mt-3 text-lg font-bold text-ink">You’re in!</h2>
            <p className="mt-1 text-[13px] text-muted">
              You joined <strong className="text-ink">{peek.workspaceName}</strong> as{' '}
              <span className="capitalize">{peek.role}</span>.
            </p>
            <button
              type="button"
              onClick={() => navigate('/billing/credits')}
              className="btn-primary mt-6 px-4 py-2 text-[13px]"
            >
              Go to the workspace
              <ArrowRight size={14} />
            </button>
          </>
        ) : !isAuthenticated ? (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
              <Mail size={22} />
            </div>
            <h2 className="text-lg font-bold text-ink">You’re invited</h2>
            <p className="mt-1 text-[13px] text-muted">
              <strong className="text-ink">{peek.workspaceName}</strong> wants you to join as{' '}
              <span className="capitalize">{peek.role}</span>. Sign in with{' '}
              <strong className="text-ink">{peek.email}</strong> to accept.
            </p>
            <Link
              to="/login"
              className="btn-primary mt-6 inline-flex items-center gap-2 px-4 py-2 text-[13px]"
            >
              <LogIn size={14} />
              Sign in to accept
            </Link>
          </>
        ) : (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
              <Mail size={22} />
            </div>
            <h2 className="text-lg font-bold text-ink">Accept the invitation</h2>
            <p className="mt-1 text-[13px] text-muted">
              Join <strong className="text-ink">{peek.workspaceName}</strong> as{' '}
              <span className="capitalize">{peek.role}</span>. The invite is addressed to{' '}
              <strong className="text-ink">{peek.email}</strong> — you’re signed in, so redeem it
              below.
            </p>
            {acceptMutation.isError && (
              <p className="mt-3 rounded-lg border border-danger/20 bg-danger/8 px-3 py-2 text-[12px] text-danger">
                {acceptMutation.error instanceof Error
                  ? acceptMutation.error.message
                  : 'Could not accept the invite'}
              </p>
            )}
            <button
              type="button"
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending}
              className="btn-primary mt-6 px-4 py-2 text-[13px]"
            >
              {acceptMutation.isPending ? 'Accepting…' : 'Accept invitation'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Block({ icon, title, message }: { icon: React.ReactNode; title: string; message: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-bg">{icon}</div>
      <h2 className="text-lg font-bold text-ink">{title}</h2>
      <p className="mt-1 max-w-xs text-[13px] text-muted">{message}</p>
    </div>
  );
}
